// src/shared/utils/utcRange.ts
//
// UTC calendar-day range math shared by the analytics and legacy-reports
// features (ADR-003 §4). Ranges are end-EXCLUSIVE instants in UTC epoch
// seconds: `from` = UTC midnight of the first day, `to` = UTC midnight AFTER
// the last day. The serving daily tables, coverage metadata, the product
// window T, and D+3 finalization are all UTC-day-grained, so presets and
// pickers operate on UTC dates (labeled "(UTC)" where days are shown).

import { DateTime } from 'luxon';

export const UTC_RANGE_PRESETS = [
  'today',
  'yesterday',
  'last7d',
  'last30d',
  'last90d',
  'currentMonth',
  'lastMonth',
  'ytd',
  'currentYear',
  'priorYear',
  'custom',
] as const;
export type UtcRangePreset = typeof UTC_RANGE_PRESETS[number];

/** Presets whose range derives from "now"; `custom` carries explicit from/to instead. */
export type ComputableUtcRangePreset = Exclude<UtcRangePreset, 'custom'>;

/** End-exclusive UTC epoch-second range. */
export interface UtcRange {
  from: number;
  to: number;
}

export const SECONDS_PER_DAY = 86_400;

/** Gateway cap for daily-aggregate and CSV ranges. */
export const MAX_RANGE_DAYS = 1095;

/** Gateway cap for minute-grained concurrency/heatmap ranges (presence retention). */
export const HEATMAP_MAX_RANGE_DAYS = 90;

/**
 * Calendar-day zone for range math: `'utc'` (the launch default and every
 * UTC-day surface) or an IANA zone name (analytics local days, dashboard
 * ADR-009 — only when the gateway declares `features.tz`). Ranges stay
 * end-exclusive epoch-second instants either way; the zone decides which
 * midnights bound them.
 */
export type DayZone = 'utc' | string;

/**
 * Compute a preset's end-exclusive calendar-day range at the given instant.
 * "Last N days" means the N calendar days ending with today (inclusive) in
 * `zone` (UTC by default). When `floorSeconds` (the product-window date floor,
 * UTC midnight of T) is provided, `from` is clamped up to it.
 */
export function presetToRange(
  preset: ComputableUtcRangePreset,
  nowSeconds: number,
  floorSeconds?: number,
  zone: DayZone = 'utc'
): UtcRange {
  const dayStart = DateTime.fromSeconds(nowSeconds, { zone }).startOf('day');
  const tomorrow = dayStart.plus({ days: 1 });
  const monthStart = dayStart.startOf('month');
  const yearStart = dayStart.startOf('year');

  let range: UtcRange;
  switch (preset) {
    case 'today':
      range = { from: dayStart.toSeconds(), to: tomorrow.toSeconds() };
      break;
    case 'yesterday':
      range = { from: dayStart.minus({ days: 1 }).toSeconds(), to: dayStart.toSeconds() };
      break;
    case 'last7d':
      range = { from: dayStart.minus({ days: 6 }).toSeconds(), to: tomorrow.toSeconds() };
      break;
    case 'last30d':
      range = { from: dayStart.minus({ days: 29 }).toSeconds(), to: tomorrow.toSeconds() };
      break;
    case 'last90d':
      range = { from: dayStart.minus({ days: 89 }).toSeconds(), to: tomorrow.toSeconds() };
      break;
    case 'currentMonth':
      range = { from: monthStart.toSeconds(), to: monthStart.plus({ months: 1 }).toSeconds() };
      break;
    case 'lastMonth':
      range = { from: monthStart.minus({ months: 1 }).toSeconds(), to: monthStart.toSeconds() };
      break;
    case 'ytd':
      range = { from: yearStart.toSeconds(), to: tomorrow.toSeconds() };
      break;
    case 'currentYear':
      // Legacy parity: the legacy dashboard's "Current Year" spans the whole
      // calendar year (future days simply have no data).
      range = { from: yearStart.toSeconds(), to: yearStart.plus({ years: 1 }).toSeconds() };
      break;
    case 'priorYear':
      range = { from: yearStart.minus({ years: 1 }).toSeconds(), to: yearStart.toSeconds() };
      break;
  }

  if (floorSeconds !== undefined && range.from < floorSeconds) {
    range = { from: Math.min(floorSeconds, range.to), to: range.to };
  }
  return range;
}

/** Number of calendar days covered by an end-exclusive day-aligned range (24-hour units; a DST day rounds up). */
export function rangeDays(range: UtcRange): number {
  return Math.ceil((range.to - range.from) / SECONDS_PER_DAY);
}

/**
 * Snap an arbitrary instant range OUTWARD to whole calendar days in `zone`:
 * `from` down to its day's midnight, `to` (exclusive) up to the midnight
 * after the last instant. Idempotent for an already-aligned range. Used to
 * hand a local-day selection to a UTC-day surface (the covering UTC days —
 * the same rule the gateway applies server-side) and vice versa.
 */
export function snapRangeToDays(range: UtcRange, zone: DayZone): UtcRange {
  const from = DateTime.fromSeconds(range.from, { zone }).startOf('day');
  const lastInstant = DateTime.fromSeconds(range.to - 1, { zone }).startOf('day');
  return { from: from.toSeconds(), to: lastInstant.plus({ days: 1 }).toSeconds() };
}

/** Midnight (epoch seconds) opening the calendar day that contains `seconds` in `zone`. */
export function dayStartSeconds(seconds: number, zone: DayZone): number {
  return DateTime.fromSeconds(seconds, { zone }).startOf('day').toSeconds();
}

/** Midnight (epoch seconds) of the calendar day AFTER the one containing `seconds` in `zone`. */
export function nextDayStartSeconds(seconds: number, zone: DayZone): number {
  return DateTime.fromSeconds(seconds, { zone }).startOf('day').plus({ days: 1 }).toSeconds();
}

/** Clamp a range's span to at most `maxDays`, keeping the (most recent) end fixed. */
export function clampRangeSpan(range: UtcRange, maxDays: number): UtcRange {
  if (rangeDays(range) <= maxDays) return range;
  return { from: range.to - maxDays * SECONDS_PER_DAY, to: range.to };
}

/**
 * Clamp a range's end down to an exclusive upper bound (`maxToSeconds`), keeping
 * at least one day. Used by Legacy Reports to enforce the ADR-011 query boundary:
 * a range extending past the boundary is truncated (never rejected); a range
 * entirely at/after the boundary collapses to the single last day before it.
 */
export function clampRangeToBoundary(range: UtcRange, maxToSeconds: number): UtcRange {
  if (range.to <= maxToSeconds) return range;
  const to = maxToSeconds;
  const from = Math.min(range.from, to - SECONDS_PER_DAY);
  return { from, to };
}

/** UTC midnight (epoch seconds) of a `YYYY-MM-DD` UTC calendar date; null if malformed. */
export function utcDateToSeconds(date: string): number | null {
  const dt = DateTime.fromFormat(date, 'yyyy-MM-dd', { zone: 'utc' });
  return dt.isValid ? dt.toSeconds() : null;
}
